import { call, put, takeLatest } from "redux-saga/effects";
import { getAppointmentOrganizer, readMailboxOwner } from "../../helpers/organizerHelpers";
import { GET_VISITORS_FAILURE, GET_VISITORS_SUCCESS } from "../actions/index";

function* fetchVisitorsOffice() {
  try {
    const visitors = yield call(getAllAttendeesFromOffice);
    const rooms = yield call(getRoomIdentifiers);
    // Weder der Organisator noch das Postfach, in dem der Termin liegt, sind
    // Besucher — beide stehen in einem freigegebenen Kalender aber als
    // Teilnehmer in der Liste.
    const organizer = yield call(getAppointmentOrganizer);
    const mailboxOwner = yield call(readMailboxOwner);
    yield put(
      GET_VISITORS_SUCCESS(
        withoutRooms(visitors, rooms, [organizer?.email, mailboxOwner?.email]),
      ),
    );
  } catch (e) {
    yield put(GET_VISITORS_FAILURE(e.message));
  }
}

function* watchFetchVisitorsRequest() {
  yield takeLatest("GET_VISITORS_REQUEST", fetchVisitorsOffice);
}

export const visitorsDataSaga = watchFetchVisitorsRequest;

/**
 * Räume des Termins (Sitzungszimmer) — sie stehen in Outlook zwar als
 * Teilnehmer in der Liste, sind aber keine Besucher und bekommen keine
 * Einladung. Die verlässliche Quelle dafür ist die Standort-Information des
 * Termins, wo Räume den Typ `Room` tragen.
 */
function getRoomIdentifiers() {
  return new Promise((resolve) => {
    const enhancedLocation = Office.context.mailbox.item?.enhancedLocation;
    if (!enhancedLocation?.getAsync) {
      resolve({ emails: [], names: [] });
      return;
    }

    enhancedLocation.getAsync((result) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        resolve({ emails: [], names: [] });
        return;
      }

      const rooms = (result.value || []).filter(
        (place) => place.locationIdentifier?.type === Office.MailboxEnums.LocationType.Room,
      );

      resolve({
        emails: rooms.map((r) => (r.locationIdentifier?.id || "").toLowerCase()).filter(Boolean),
        // Ältere Postfächer liefern keine Adresse am Raum — dann bleibt der
        // Anzeigename als Vergleich.
        names: rooms.map((r) => (r.displayName || "").trim().toLowerCase()).filter(Boolean),
      });
    });
  });
}

function withoutRooms(attendees, rooms, excludedEmails) {
  const excluded = (excludedEmails || []).filter(Boolean).map((email) => email.trim().toLowerCase());
  if (!rooms.emails.length && !rooms.names.length && !excluded.length) return attendees;
  return attendees.filter((attendee) => {
    const email = (attendee.email || "").toLowerCase();
    const name = (attendee.name || "").trim().toLowerCase();
    if (excluded.includes(email)) return false;
    return !rooms.emails.includes(email) && !rooms.names.includes(name);
  });
}

function getAllAttendeesFromOffice() {
  return new Promise((resolve, reject) => {
    let allAttendees = [];

    Office.context.mailbox.item.requiredAttendees.getAsync((asyncResult) => {
      if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
        allAttendees = allAttendees.concat(
          asyncResult.value.map((attendee) => ({
            name: attendee.displayName,
            email: attendee.emailAddress,
            isChecked: true,
          })),
        );

        Office.context.mailbox.item.optionalAttendees.getAsync((optionalAsyncResult) => {
          if (optionalAsyncResult.status === Office.AsyncResultStatus.Succeeded) {
            allAttendees = allAttendees.concat(
              optionalAsyncResult.value.map((attendee) => ({
                name: attendee.displayName,
                email: attendee.emailAddress,
                isChecked: false,
              })),
            );
            resolve(allAttendees);
          } else {
            reject(new Error("Fehler beim Abrufen optionaler Teilnehmer"));
          }
        });
      } else {
        reject(new Error("Fehler beim Abrufen erforderlicher Teilnehmer"));
      }
    });
  });
}
